import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getPantry, savePantry } from "../api/pantry";
import { getMeals } from "../api/meals";
import { AuthContext } from "../context/AuthContext";

const EMPTY_INPUT = { name:"", quantity:"", unit:"" };
const SPOONACULAR_KEY = process.env.REACT_APP_SPOONACULAR_KEY;

// Returns { canMake, almostThere } arrays, each element: { meal, missing[] }
function matchMeals(pantryItems, meals){
  const pantryMap = new Map();
  for(const item of pantryItems){
    const key = item.name.trim().toLowerCase();
    if(!key) continue;
    pantryMap.set(key, {
      quantity: parseFloat(item.quantity) || null,
      unit:     (item.unit || "").trim().toLowerCase()
    });
  }

  const canMake     = [];
  const almostThere = [];

  for(const meal of meals){
    const rawIngredients = meal.ingredients || [];
    const ingredients = rawIngredients
      .map(i => typeof i === "string" ? { name:i, quantity:"", unit:"" } : i)
      .filter(i => (i.name || "").trim());

    if(ingredients.length === 0) continue;

    const missing = [];

    for(const ing of ingredients){
      const key = ing.name.trim().toLowerCase();
      const pantryItem = pantryMap.get(key);

      if(!pantryItem){
        missing.push({ name: ing.name, quantity: ing.quantity, unit: ing.unit });
        continue;
      }

      const required = parseFloat(ing.quantity);
      const have     = pantryItem.quantity;
      const sameUnit = !ing.unit || !pantryItem.unit ||
        ing.unit.trim().toLowerCase() === pantryItem.unit;

      if(!isNaN(required) && have !== null && sameUnit && have < required){
        missing.push({
          name: ing.name,
          quantity: ing.quantity,
          unit: ing.unit,
          have: pantryItem.quantity,
          insufficientQty: true
        });
      }
    }

    if(missing.length === 0){
      canMake.push({ meal, missing });
    } else if(missing.length <= 2){
      almostThere.push({ meal, missing });
    }
  }

  almostThere.sort((a,b) => a.missing.length - b.missing.length);

  return { canMake, almostThere };
}

function formatMissing(ing){
  const parts = [ing.quantity, ing.unit, ing.name].filter(Boolean);
  return parts.join(" ");
}

function recipeSlug(title){
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export default function PantryPage({ meals, setMeals }){

  const { householdId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [pantryItems, setPantryItems]   = useState([]);
  const [input, setInput]               = useState({ ...EMPTY_INPUT });
  const [loading, setLoading]           = useState(true);
  const [webToggle, setWebToggle]       = useState(false);
  const [webResults, setWebResults]     = useState([]);
  const [webLoading, setWebLoading]     = useState(false);
  const [webError, setWebError]         = useState(null);

  useEffect(()=>{
    if(!householdId) return;
    async function load(){
      const [items, mealData] = await Promise.all([
        getPantry(householdId),
        meals.length > 0 ? Promise.resolve(meals) : getMeals(householdId)
      ]);
      setPantryItems(items);
      if(mealData !== meals) setMeals(mealData);
      setLoading(false);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[householdId]);

  // Search Spoonacular whenever the toggle turns on (if there are ingredients)
  useEffect(()=>{
    if(!webToggle) return;
    if(pantryItems.length === 0) return;
    searchWeb();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[webToggle]);

  async function searchWeb(){
    const names = pantryItems.map(i => i.name.trim()).filter(Boolean);
    if(names.length === 0) return;
    setWebLoading(true);
    setWebError(null);
    try{
      const query = names.join(",");
      const url = `https://api.spoonacular.com/recipes/findByIngredients?ingredients=${encodeURIComponent(query)}&number=10&ranking=1&ignorePantry=true&apiKey=${SPOONACULAR_KEY}`;
      const res = await fetch(url);
      if(!res.ok) throw new Error(`Spoonacular error ${res.status}`);
      const data = await res.json();
      setWebResults(data);
    } catch(err){
      setWebError("Could not load web recipes. Try again later.");
    } finally {
      setWebLoading(false);
    }
  }

  async function handleAdd(){
    const name = input.name.trim();
    if(!name) return;
    const newItems = [...pantryItems, { ...input, name }];
    setPantryItems(newItems);
    setInput({ ...EMPTY_INPUT });
    await savePantry(householdId, newItems);
  }

  async function handleRemove(index){
    const newItems = pantryItems.filter((_,i)=>i!==index);
    setPantryItems(newItems);
    await savePantry(householdId, newItems);
  }

  async function handleClearAll(){
    setPantryItems([]);
    setWebResults([]);
    await savePantry(householdId, []);
  }

  function handleKeyDown(e){
    if(e.key === "Enter") handleAdd();
  }

  const { canMake, almostThere } = useMemo(()=>
    matchMeals(pantryItems, meals)
  ,[pantryItems, meals]);

  const pantryEmpty   = pantryItems.length === 0;
  const mealsEmpty    = meals.length === 0;
  const noSuggestions = canMake.length === 0 && almostThere.length === 0;

  return(

    <div>

      <div className="pageHeader">
        <h2>Cook with My Ingredients</h2>
      </div>

      <div className="pantryLayout">

        {/* ── LEFT: INGREDIENTS ── */}
        <div>

          <div className="card">

            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
              <h3 style={{margin:0}}>My Ingredients</h3>
              {pantryItems.length > 0 && (
                <button className="clearAllBtn" onClick={handleClearAll}>
                  Clear all
                </button>
              )}
            </div>

            {/* ADD FORM */}
            <div className="pantryAddRow">
              <input
                placeholder="Ingredient"
                value={input.name}
                onChange={e=>setInput(p=>({...p, name:e.target.value}))}
                onKeyDown={handleKeyDown}
              />
              <input
                placeholder="Qty"
                inputMode="decimal"
                value={input.quantity}
                onChange={e=>setInput(p=>({...p, quantity:e.target.value}))}
                onKeyDown={handleKeyDown}
              />
              <input
                placeholder="Unit"
                value={input.unit}
                onChange={e=>setInput(p=>({...p, unit:e.target.value}))}
                onKeyDown={handleKeyDown}
              />
              <button onClick={handleAdd}>Add</button>
            </div>

            {pantryEmpty && !loading && (
              <p className="textMuted" style={{fontSize:14, marginTop:14}}>
                No ingredients yet. Add some above to see what you can make.
              </p>
            )}

            <div className="pantryList">
              {pantryItems.map((item, i)=>(
                <div key={i} className="pantryItem">
                  <span className="pantryItemName">{item.name}</span>
                  {(item.quantity || item.unit) && (
                    <span className="pantryItemQty">
                      {[item.quantity, item.unit].filter(Boolean).join(" ")}
                    </span>
                  )}
                  <button
                    className="calRemoveBtn"
                    onClick={()=>handleRemove(i)}
                  >×</button>
                </div>
              ))}
            </div>

          </div>

          {/* WEB TOGGLE */}
          <div className="card" style={{marginTop:16}}>
            <div className="webToggleRow">
              <div>
                <div className="webToggleLabel">Search web recipes</div>
                <div className="webToggleDesc">
                  Find recipes from the internet using your ingredients
                </div>
              </div>
              <button
                className={`toggle ${webToggle ? "toggle--on" : ""}`}
                onClick={()=> setWebToggle(v => !v)}
                aria-pressed={webToggle}
              >
                <span className="toggleThumb" />
              </button>
            </div>
          </div>

        </div>

        {/* ── RIGHT: RESULTS ── */}
        <div>

          {/* LIBRARY MATCHES */}
          {mealsEmpty && (
            <div className="card textMuted">
              No meals in your library.{" "}
              <a href="/meals" className="linkOlive">Add some meals first.</a>
            </div>
          )}

          {!mealsEmpty && pantryEmpty && (
            <div className="card textMuted">
              Add ingredients to see what you can make from your meal library.
            </div>
          )}

          {!mealsEmpty && !pantryEmpty && noSuggestions && (
            <div className="card textMuted">
              No meals from your library match your ingredients. Try adding more.
            </div>
          )}

          {canMake.length > 0 && (
            <div className="suggestionGroup">
              <div className="suggestionGroupHeader suggestionGroupHeader--can">
                Can Make
                <span className="suggestionCount">{canMake.length}</span>
              </div>
              {canMake.map(({meal})=>(
                <SuggestionCard
                  key={meal.id}
                  meal={meal}
                  missing={[]}
                  onAddToPlanner={()=>navigate(`/meals/plan/${meal.id}`)}
                />
              ))}
            </div>
          )}

          {almostThere.length > 0 && (
            <div className="suggestionGroup">
              <div className="suggestionGroupHeader suggestionGroupHeader--almost">
                Almost There
                <span className="suggestionCount">{almostThere.length}</span>
              </div>
              {almostThere.map(({meal, missing})=>(
                <SuggestionCard
                  key={meal.id}
                  meal={meal}
                  missing={missing}
                  onAddToPlanner={()=>navigate(`/meals/plan/${meal.id}`)}
                />
              ))}
            </div>
          )}

          {/* WEB RESULTS */}
          {webToggle && (
            <div style={{marginTop: (canMake.length > 0 || almostThere.length > 0) ? 8 : 0}}>

              <div className="suggestionGroupHeader suggestionGroupHeader--web">
                From the Web
                {!webLoading && webResults.length > 0 && (
                  <span className="suggestionCount">{webResults.length}</span>
                )}
                {!webLoading && (
                  <button className="webRefreshBtn" onClick={searchWeb}>
                    ↻ Refresh
                  </button>
                )}
              </div>

              {pantryEmpty && (
                <div className="card textMuted" style={{borderRadius:"0 0 8px 8px"}}>
                  Add ingredients first to search web recipes.
                </div>
              )}

              {webLoading && (
                <div className="card textMuted" style={{borderRadius:"0 0 8px 8px"}}>
                  Searching recipes…
                </div>
              )}

              {webError && !webLoading && (
                <div className="card textMuted" style={{borderRadius:"0 0 8px 8px"}}>
                  {webError}
                </div>
              )}

              {!webLoading && !webError && webResults.length === 0 && !pantryEmpty && (
                <div className="card textMuted" style={{borderRadius:"0 0 8px 8px"}}>
                  No web recipes found for your ingredients.
                </div>
              )}

              {!webLoading && webResults.map(recipe=>(
                <WebRecipeCard key={recipe.id} recipe={recipe} />
              ))}

            </div>
          )}

        </div>

      </div>

    </div>

  );

}

function SuggestionCard({ meal, missing, onAddToPlanner }){
  return(
    <div className="suggestionCard">

      <div className="suggestionCardHeader">
        <div>
          <h3 className="suggestionCardTitle">{meal.name}</h3>
          {meal.servings && (
            <span className="mealServes">Serves {meal.servings}</span>
          )}
        </div>
        <button className="addToPlannerBtn" onClick={onAddToPlanner}>
          + Planner
        </button>
      </div>

      {(meal.tags||[]).length > 0 && (
        <div className="mealTags" style={{marginBottom:6}}>
          {meal.tags.map(tag=>(
            <span key={tag} className="mealTag">{tag}</span>
          ))}
        </div>
      )}

      {missing.length > 0 && (
        <div className="missingIngredients">
          <span className="missingLabel">Missing:</span>
          {missing.map((ing,i)=>(
            <span key={i} className="missingItem">
              {formatMissing(ing)}
              {ing.insufficientQty && ` (have ${ing.have} ${ing.unit})`}
            </span>
          ))}
        </div>
      )}

    </div>
  );
}

function WebRecipeCard({ recipe }){
  const used   = recipe.usedIngredients  || [];
  const missed = recipe.missedIngredients || [];
  const href   = `https://spoonacular.com/recipes/${recipeSlug(recipe.title)}-${recipe.id}`;

  return(
    <div className="suggestionCard webRecipeCard">

      <div className="suggestionCardHeader">
        <div style={{display:"flex", gap:12, alignItems:"flex-start"}}>
          {recipe.image && (
            <img
              src={recipe.image}
              alt={recipe.title}
              className="webRecipeThumb"
            />
          )}
          <div>
            <h3 className="suggestionCardTitle">{recipe.title}</h3>
            <span className="mealServes">
              {used.length} ingredient{used.length !== 1 ? "s" : ""} you have
              {missed.length > 0 && ` · missing ${missed.length}`}
            </span>
          </div>
        </div>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="addToPlannerBtn"
          style={{textDecoration:"none", whiteSpace:"nowrap"}}
        >
          View Recipe
        </a>
      </div>

      {missed.length > 0 && (
        <div className="missingIngredients">
          <span className="missingLabel">Missing:</span>
          {missed.map((ing,i)=>(
            <span key={i} className="missingItem">{ing.name}</span>
          ))}
        </div>
      )}

    </div>
  );
}
